import React, { useEffect, useRef, useState } from 'react';
import './DynamicHero.css';

const DynamicHero = () => {
  const canvasRef = useRef(null);
  const shieldCanvasRef = useRef(null);
  const containerRef = useRef(null);
  const [mousePos, setMousePos] = useState({ x: 0, y: 0 });

  // AI Circuit Animation
  useEffect(() => {
    const canvas = canvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    // Circuit nodes
    class CircuitNode {
      constructor(x, y) {
        this.x = x;
        this.y = y;
        this.pulse = Math.random() * Math.PI * 2;
        this.speed = 0.02 + Math.random() * 0.02;
      }

      update() {
        this.pulse += this.speed;
      }

      draw() {
        const size = 3 + Math.sin(this.pulse) * 1;
        ctx.beginPath();
        ctx.arc(this.x, this.y, size, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(102, 126, 234, ${0.6 + Math.sin(this.pulse) * 0.3})`;
        ctx.fill();
        
        // Glow effect
        ctx.shadowBlur = 10;
        ctx.shadowColor = 'rgba(102, 126, 234, 0.8)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    // Circuit connections
    class CircuitConnection {
      constructor(node1, node2) {
        this.node1 = node1;
        this.node2 = node2;
        this.pulse = Math.random() * Math.PI * 2;
        this.speed = 0.03;
      }

      update() {
        this.pulse += this.speed;
      }

      draw() {
        const distance = Math.sqrt(
          Math.pow(this.node2.x - this.node1.x, 2) + 
          Math.pow(this.node2.y - this.node1.y, 2)
        );

        if (distance > 200) return;

        const progress = (Math.sin(this.pulse) + 1) / 2;
        const x = this.node1.x + (this.node2.x - this.node1.x) * progress;
        const y = this.node1.y + (this.node2.y - this.node1.y) * progress;

        ctx.beginPath();
        ctx.moveTo(this.node1.x, this.node1.y);
        ctx.lineTo(this.node2.x, this.node2.y);
        ctx.strokeStyle = `rgba(102, 126, 234, ${0.2 + progress * 0.3})`;
        ctx.lineWidth = 1;
        ctx.stroke();

        // Animated pulse along the line
        ctx.beginPath();
        ctx.arc(x, y, 3, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(102, 126, 234, ${0.8 + progress * 0.2})`;
        ctx.fill();
        ctx.shadowBlur = 8;
        ctx.shadowColor = 'rgba(102, 126, 234, 1)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    // Create nodes
    const nodes = [];
    const nodeCount = 30;
    for (let i = 0; i < nodeCount; i++) {
      nodes.push(new CircuitNode(
        Math.random() * canvas.width,
        Math.random() * canvas.height
      ));
    }

    // Create connections
    const connections = [];
    for (let i = 0; i < nodes.length; i++) {
      for (let j = i + 1; j < nodes.length; j++) {
        const distance = Math.sqrt(
          Math.pow(nodes[j].x - nodes[i].x, 2) + 
          Math.pow(nodes[j].y - nodes[i].y, 2)
        );
        if (distance < 150) {
          connections.push(new CircuitConnection(nodes[i], nodes[j]));
        }
      }
    }

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);

      nodes.forEach(node => {
        node.update();
        node.draw();
      });

      connections.forEach(conn => {
        conn.update();
        conn.draw();
      });

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      cancelAnimationFrame(animationFrameId);
    };
  }, []);

  // Shield with R and Data Points
  useEffect(() => {
    const canvas = shieldCanvasRef.current;
    if (!canvas) return;

    const ctx = canvas.getContext('2d');
    let animationFrameId;
    
    const resizeCanvas = () => {
      canvas.width = canvas.offsetWidth;
      canvas.height = canvas.offsetHeight;
    };

    resizeCanvas();
    window.addEventListener('resize', resizeCanvas);

    let time = 0;
    let morphPhase = 0;

    // Data points
    class DataPoint {
      constructor() {
        this.angle = Math.random() * Math.PI * 2;
        this.radius = 80 + Math.random() * 40;
        this.speed = 0.01 + Math.random() * 0.02;
        this.size = 2 + Math.random() * 2;
        this.pulse = Math.random() * Math.PI * 2;
      }

      update() {
        this.angle += this.speed;
        this.pulse += 0.05;
      }

      draw(centerX, centerY) {
        const x = centerX + Math.cos(this.angle) * this.radius;
        const y = centerY + Math.sin(this.angle) * this.radius;
        const opacity = 0.5 + Math.sin(this.pulse) * 0.5;
        const currentSize = this.size + Math.sin(this.pulse) * 1;

        ctx.beginPath();
        ctx.arc(x, y, currentSize, 0, Math.PI * 2);
        ctx.fillStyle = `rgba(102, 126, 234, ${opacity})`;
        ctx.fill();
        ctx.shadowBlur = 6;
        ctx.shadowColor = 'rgba(102, 126, 234, 0.8)';
        ctx.fill();
        ctx.shadowBlur = 0;
      }
    }

    const dataPoints = [];
    for (let i = 0; i < 20; i++) {
      dataPoints.push(new DataPoint());
    }

    const handleMouseMove = (e) => {
      const rect = canvas.getBoundingClientRect();
      setMousePos({
        x: e.clientX - rect.left,
        y: e.clientY - rect.top
      });
    };

    canvas.addEventListener('mousemove', handleMouseMove);

    const animate = () => {
      ctx.clearRect(0, 0, canvas.width, canvas.height);
      
      time += 0.02;
      morphPhase += 0.01;

      const centerX = canvas.width / 2;
      const centerY = canvas.height / 2;

      // Calculate mouse influence
      const mouseDistance = Math.sqrt(
        Math.pow(mousePos.x - centerX, 2) + 
        Math.pow(mousePos.y - centerY, 2)
      );
      const mouseInfluence = Math.max(0, 1 - mouseDistance / 200);
      const glowIntensity = 0.5 + mouseInfluence * 0.5;

      // Draw shield with morphing effect
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // Shield shape with morphing
      const shieldPoints = 8;
      const baseRadius = 100;
      const morphAmount = Math.sin(morphPhase) * 15;
      
      ctx.beginPath();
      for (let i = 0; i < shieldPoints; i++) {
        const angle = (i / shieldPoints) * Math.PI * 2 - Math.PI / 2;
        const radius = baseRadius + Math.sin(morphPhase + i) * morphAmount;
        const x = Math.cos(angle) * radius;
        const y = Math.sin(angle) * radius;
        
        if (i === 0) {
          ctx.moveTo(x, y);
        } else {
          ctx.lineTo(x, y);
        }
      }
      ctx.closePath();

      // Shield gradient
      const gradient = ctx.createRadialGradient(0, 0, 0, 0, 0, baseRadius);
      gradient.addColorStop(0, `rgba(102, 126, 234, ${0.3 * glowIntensity})`);
      gradient.addColorStop(0.5, `rgba(118, 75, 162, ${0.2 * glowIntensity})`);
      gradient.addColorStop(1, `rgba(102, 126, 234, ${0.1 * glowIntensity})`);
      
      ctx.fillStyle = gradient;
      ctx.fill();

      // Shield border with glow
      ctx.strokeStyle = `rgba(102, 126, 234, ${0.8 * glowIntensity})`;
      ctx.lineWidth = 3;
      ctx.shadowBlur = 20 * glowIntensity;
      ctx.shadowColor = 'rgba(102, 126, 234, 1)';
      ctx.stroke();
      ctx.shadowBlur = 0;

      ctx.restore();

      // Draw data points
      dataPoints.forEach(point => {
        point.update();
        point.draw(centerX, centerY);
      });

      // Draw glowing R
      ctx.save();
      ctx.translate(centerX, centerY);
      
      // R letter with glow effect - draw multiple layers for better glow
      const fontSize = 80 + Math.sin(time) * 5;
      ctx.font = `bold ${fontSize}px Arial, sans-serif`;
      ctx.textAlign = 'center';
      ctx.textBaseline = 'middle';
      
      const rGlow = glowIntensity * 30;
      
      // Draw multiple glow layers
      for (let i = 5; i >= 0; i--) {
        ctx.shadowBlur = rGlow - i * 4;
        ctx.shadowColor = `rgba(102, 126, 234, ${0.9 - i * 0.15})`;
        ctx.fillStyle = `rgba(255, 255, 255, ${0.3 + (5 - i) * 0.1})`;
        ctx.fillText('R', 0, 0);
      }
      
      // Final R letter on top
      ctx.shadowBlur = 0;
      ctx.fillStyle = `rgba(255, 255, 255, ${0.95 + mouseInfluence * 0.05})`;
      ctx.fillText('R', 0, 0);

      ctx.restore();

      animationFrameId = requestAnimationFrame(animate);
    };

    animate();

    return () => {
      window.removeEventListener('resize', resizeCanvas);
      canvas.removeEventListener('mousemove', handleMouseMove);
      cancelAnimationFrame(animationFrameId);
    };
  }, [mousePos]);

  return (
    <section className="dynamic-hero" ref={containerRef}>
      <canvas ref={canvasRef} className="circuit-canvas" />
      <div className="dynamic-hero-content">
        <div className="hero-text-container">
          <h1 className="hero-main-title">
            <span className="title-line">Securing the Future.</span>
            <span className="title-line gradient-text">Powered by Intelligence.</span>
          </h1>
          <p className="hero-subtitle">
            AI-driven cybersecurity solutions protecting enterprises with resilient, intelligent defense
          </p>
        </div>
        <div className="shield-container">
          <canvas ref={shieldCanvasRef} className="shield-canvas" />
        </div>
      </div>
    </section>
  );
};

export default DynamicHero;

